// Helper untuk margin/HPP & konversi paket -> pcs.
//
// FAKTA YANG SUDAH DIKONFIRMASI LANGSUNG DARI DATA `order_items` (bukan asumsi):
//
//   "Paket Bertiga (3x Cireng Isi Ayam Pedas)"  qty=1  unit_price=7000  hpp_snapshot=2000
//   "Paket Berlima (5x Cireng Isi Ayam Pedas)"  qty=1  unit_price=12000 hpp_snapshot=2000
//
// Untuk baris PAKET, qty selalu dicatat 1 (artinya "1 paket terjual", bukan
// jumlah pcs di dalamnya), dan hpp_snapshot ternyata cuma HPP SATU komponen
// dasarnya (2000), bukan dikali isi paketnya (3x atau 5x). Akibatnya:
//   - Margin paket di Dashboard/Analytics jadi OVERSTATED (mis. "Bertiga"
//     dilaporkan margin 71% padahal HPP aslinya 3×2000=6000, margin asli ~14%).
//   - Hitungan "pcs terjual" (dipakai bonus karyawan & kebutuhan produksi)
//     ikut UNDERCOUNT kalau hanya membaca qty mentah dari order_items.
//
// Untuk baris BUKAN paket (item biasa), hpp_snapshot & margin_amount yang
// tersimpan SUDAH benar (sudah dikali qty dengan tepat) — jadi untuk baris
// ini kita tetap percaya snapshot-nya, kecuali snapshotnya 0 (order lama
// sebelum HPP pernah diisi), baru fallback ke HPP master saat ini.

// Lookup "nama item" -> HPP per pcs (hanya yang HPP-nya > 0)
export function buildMenuHppMap(menuItems = []) {
  const map = {};
  menuItems.forEach(m => {
    if (m?.name && m.hpp > 0) map[m.name.trim()] = m.hpp;
  });
  return map;
}

// Lookup "nama paket" -> { hppTotal, pcsTotal }
//   hppTotal = total HPP semua komponen di dalam 1 paket (untuk margin)
//   pcsTotal = total pcs semua komponen di dalam 1 paket (untuk produksi & bonus)
export function buildPackageMap(packages = []) {
  const map = {};
  packages.forEach(p => {
    const items = p.package_items || [];
    if (!items.length || !p?.name) return;
    const pcsTotal = items.reduce((s, pi) => s + (pi.qty || 0), 0);
    const allHaveHpp = items.every(pi => (pi.menu_items?.hpp || 0) > 0);
    const hppTotal = allHaveHpp
      ? items.reduce((s, pi) => s + (pi.menu_items?.hpp || 0) * pi.qty, 0)
      : null; // null = jangan dipakai untuk margin (ada komponen tanpa HPP)
    map[p.name.trim()] = { hppTotal, pcsTotal };
  });
  return map;
}

// order_items.display_name biasanya berformat "Nama Item – Nama Varian"
export function baseNameFromDisplay(displayName = '') {
  return (displayName || '').split(' – ')[0].trim();
}

// Hitung HPP per-baris (sudah mewakili qty di baris itu, bukan per-pcs) untuk satu order_item.
// Return { hpp, isLive } — isLive true kalau bukan dari snapshot asli (paket dikoreksi, atau fallback).
export function resolveLineHpp(item, packageMap, menuHppMap) {
  const name = packageMap[baseNameFromDisplay(item.display_name)] ? baseNameFromDisplay(item.display_name) : null;
  const pkg = name ? packageMap[name] : null;

  // Baris PAKET: snapshot yang tersimpan terbukti salah (lihat catatan di atas),
  // jadi selalu pakai hitungan HPP paket yang benar dari Menu Paket saat ini.
  if (pkg && pkg.hppTotal > 0) {
    return { hpp: pkg.hppTotal * item.qty, isLive: true };
  }

  // Baris biasa: snapshot sudah benar, percaya snapshot kalau ada.
  if (item.hpp_snapshot > 0) {
    return { hpp: item.hpp_snapshot * item.qty, isLive: false };
  }

  // Snapshot kosong (order lama sebelum HPP diisi) -> fallback ke HPP master saat ini.
  const liveUnit = menuHppMap[baseNameFromDisplay(item.display_name)];
  if (liveUnit > 0) {
    return { hpp: liveUnit * item.qty, isLive: true };
  }

  return { hpp: 0, isLive: false };
}

// Hitung pcs ASLI untuk satu order_item — paket otomatis "dipecah" ke isinya.
// Misal qty=1 "Paket Bertiga (3x Cireng Isi)" -> 3 pcs, bukan 1.
export function resolvePcs(item, packageMap) {
  const name = baseNameFromDisplay(item.display_name);
  const pkg = packageMap[name];
  if (pkg && pkg.pcsTotal > 0) return pkg.pcsTotal * item.qty;
  return item.qty;
}
